import React, { useRef, useEffect, useState } from 'react';

import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { ModelBase } from "@models";
import { 
  BastionFacility,
  Character,
  CharacterBastionFacility,
} from "@dndModels";

// import DataUtilities from '../../../utilities/common_utilities';
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import SelectObjectIndex from "@dndComponents/select/SelectObjectIndex";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CharacterBastionFacilityInput from "@dndComponents/character/bastions/CharacterBastionFacilityInput";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  char: Character;
}

// const common_util = DataUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

const specials_for_char_level = {
  0: 0, 5: 2, 9: 4, 13: 5, 17: 6
};
const special_levels = [0,5,9,13,17];

export default function CharacterBastionInput({
  char,
}: Props) {
  const [fetched, setFetched] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [new_bastion_facility, setNewBastionFacility] = useState(false);
  const [filter, setFilter] = useState<any>({ });
  const [expanded_bf_id, setExpandedBastionFacilityID] = useState(char.spirit.gifts.length === 1 ? char.spirit.gifts[0].true_id : "");
  // const [change_time, setChangeTime] = useState(0);
  const [show_desc, setShowDesc] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const bastionChanged = () => {
    setReloading(true);
  }
  
  useEffect(() => {
    if (!fetched) {
      char.subscription_holder.subscribe("Bastion", mounted, bastionChanged);
      setFetched(true);
    }
  }, [fetched, char]);

  // useEffect(() => {
  //   if (!fetched) {
  //     setFetched(true);
  //     setLoading(true);
  //     char_util.getBastionFacilities().then(res => {
  //       setBastionFacilities(res);
  //     });
  //   }
  // });

  const updateFilter = (changed: any = {}) => {
    setFilter({...changed});
  }

  const renderAddAnotherBastionFacility = () => {
    return (
      <div style={{ float: "right" }}>
        <AddButton
          label={char.spirit.bastion_facilities.length > 0 ? "Add Another Facility" : "Add a Bastion Facility" }
          onClick={() => {
            setNewBastionFacility(true);
          }} 
        />
      </div>
    );
  }

  const renderBastionFacilities = () => {
    return (
      <Grid container spacing={0} direction="row">
        { char.spirit.bastion_facilities.map(char_bf => {
          return (
            <Grid
              key={char_bf.true_id}
              size={{
                xs: 12
              }}>
              { renderBastionFacility(char_bf) }
            </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderBastionFacility = (char_bf: CharacterBastionFacility) => {
    return (
      <Grid container spacing={0} direction="row" className="MyBorder">
        <Grid
          container
          spacing={0}
          direction="row"
          className="clickable padded header"
          onClick={() => {
            setExpandedBastionFacilityID(expanded_bf_id === char_bf.true_id ? "" : char_bf.true_id);
          }}
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 12
            }}>
            { char_bf.name }
          </Grid>
          <Grid
            className="small_label gray"
            size={{
              xs: 12
            }}>
            { char_bf.source_name !== char_bf.name ? char_bf.source_name : "" }&nbsp;
            { char_bf.size }
          </Grid>
        </Grid>
        { expanded_bf_id === char_bf.true_id &&
          <CharacterBastionFacilityInput
            char={char}
            char_bf={char_bf}
            onDelete={ async () => {
              await connect_util.deleteBastionFacility(char, char_bf);
            }}
          />
        }
      </Grid>
    );
  }

  const renderBastionsDescription = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <ToggleButtonBox
            name="Show Bastion Description"
            value={show_desc}
            onToggle={() => { setShowDesc(!show_desc); }}
          />
        </Grid>
        { show_desc &&
          <>
            <Grid
              size={{
                xs: 12
              }}>
              A character’s Bastion starts with two free basic facilities, 
              which the character’s player chooses from the Basic Facilities 
              list below. One of the chosen facilities is Cramped, 
              and the other is Roomy (see the Facility Space table). 
              A Bastion can have more than one of each basic facility.
              
              A basic facility comes with nonmagical furnishings and decor 
              appropriate for that facility.
              Basic facilities don’t have any game effects, 
              but they can inspire roleplaying opportunities and enhance 
              a Bastion’s verisimilitude. 
              A Bastion with a kitchen is functionally the same as one 
              without, but the former gives you and your players a fun 
              setting to start game sessions, have in-character 
              discussions, or introduce new NPCs.
              A character can add new basic facilities or enlarge 
              existing ones by spending money and time, 
              as discussed in the sections that follow. 
              Any number of basic facilities can be added or enlarged 
              at the same time. 
              A character doesn’t need to be in their Bastion while 
              basic facilities are being added or enlarged.
              Adding Basic Facilities
              A character can add a basic facility to their Bastion by 
              spending money and time. 
              The cost of adding a basic facility and the time required 
              depend on the facility’s space, as shown on the 
              table below.

              <table>
                <tr><th>Facility Space</th><th>Cost</th><th>Time Required</th></tr>
                <tr><td>Cramped</td><td>500 GP</td><td>20 days</td></tr>
                <tr><td>Roomy</td><td>1,000 GP</td><td>45 days</td></tr>
                <tr><td>Vast</td><td>3,000 GP</td><td>125 days</td></tr>
              </table>
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              Special facilities are Bastion locations where certain 
              activities yield game benefits. 
              A character’s Bastion initially has two special facilities 
              of the character’s choice for which they qualify. 
              Each special facility can be chosen only once unless its 
              description says otherwise.
              Unlike basic facilities, special facilities can’t be bought; 
              a character gains them through level advancement. 
              At level 9, a character gains two additional 
              special facilities of their choice for which they qualify; 
              they gain one additional facility at level 13 and another 
              at level 17. The Special Facility Acquisition table shows 
              the total number of special facilities in a character’s 
              Bastion. 
              Each new special facility immediately becomes part of the 
              character’s Bastion when the character reaches the level.
              Each time a character gains a level, that character can 
              replace one of their Bastion’s special facilities with 
              another for which the character qualifies.

              <b>Special Facility Acquisition</b>
              <table>
                <tr><th>Level</th><th>Special Facilities</th></tr>
                <tr><td>5</td><td>2</td></tr>
                <tr><td>9</td><td>4</td></tr>
                <tr><td>13</td><td>5</td></tr>
                <tr><td>17</td><td>6</td></tr>
              </table>
            </Grid>
          </>
        }
      </>
    );
  }

  // debugger;
  if (char.spirit.bastion_facilities.length === 0 || new_bastion_facility) {
    const num_specials = char.spirit.bastion_facilities.filter(o => o.special_facility).length;
    const potence_level = Math.max(...special_levels.filter(o => o <= char.mind.character_level)) as (0 | 5 | 9 | 13 | 17);
    const max_specials = specials_for_char_level[potence_level];
    // const num_basic = char.bastion_facilities.filter(o => !o.special_facility).length;
    return (
      <Grid container spacing={0} direction="row">
        { new_bastion_facility && 
          <Grid
            size={{
              xs: 12
            }}>
            <Button variant="contained" color="primary"
              onClick={() => {
                setNewBastionFacility(false);
              }}>
              Cancel Add
            </Button>
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <SelectObjectIndex
            data_type="bastion_facility"
            character={char}
            blocked_ids={char.c_options.blocked}
            filter={filter}
            updateFilter={updateFilter}
            quick_fields={["bastion_size","special_facility"]}
            // log_it
            onClick={async (obj: ModelBase) => {
              if (obj instanceof BastionFacility) {
                const char_bf = await connect_util.addBastionFacility(char, obj);
                setNewBastionFacility(false);
                setExpandedBastionFacilityID(char_bf.true_id);
              }
            }}
            disableObj={(obj: ModelBase) => {
              let disabled = false;
              let reasons = "";
              const char_level = char.mind.character_level;
              if (char_level < 5) {
                disabled = true;
                reasons = "Bastions become unlocked at level 5.";
              } else if (obj instanceof BastionFacility) {
                if (!obj.repeatable && char.spirit.bastion_facilities.filter(o => 
                  o.source_id === obj._id).length > 0) { // TODO: Add in repeatable bastion facilities.
                  disabled = true;
                  reasons = "You already have this Bastion Facility";
                } else {
                  reasons = char_util.fitLimits(char, obj.limitations, "", "");
                  if (reasons !== "true") {
                    disabled = true;
                  } else if (obj.special_facility) {
                    if (num_specials >= max_specials) {
                      disabled = true;
                      reasons = "You already have your maximum number of Special Facilities.";
                    }
                  } else {
                    // TODO: Add functionality to charge for extra basics. First 2 are free.
                  }
                  if (reasons === "true") {
                    reasons = "";
                  }
                }
              }
              return { disabled, reasons };
            }}
          />
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>Bastion Facilities</Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { renderAddAnotherBastionFacility() }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { renderBastionFacilities() }
        </Grid>
        { renderBastionsDescription() }
      </Grid>
    );
  }
}
